import {
  CookieConsents,
  decodeCookieConsentsData,
  encodeCookieConsentsData,
  getCookieName,
  getCookieOptions,
  generateUidCookieValue,
} from '@creditornot/cwc-cookie-sdk';
import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useCookie } from 'react-use';

import { ManageCookieConsentsController } from './components/ManageCookieConsentsController';
import { useShowConsentsModal } from './useShowConsentsModal';
import { getCookieDomain } from '../../common/utils';
import sendErrorReport from '../../common/utils/sendErrorReport';
import { buildPrivacyTermsUrl } from '../../locale/utils';

type GdprConsentContextValue = {
  consents: CookieConsents;
  updateConsents: (consents: CookieConsents) => void;
  hasReceivedConsentEvent: boolean;
};

const CookieConsentsContext = createContext<GdprConsentContextValue | undefined>(undefined);

const WOLT_UID_COOKIE_NAME = getCookieName('wolt-uid');

export const useCookieConsents = () => {
  const value = useContext(CookieConsentsContext);
  if (value === undefined) {
    throw new Error('You can use useCookieConsents only in a child of CookieConsentsProvider');
  }

  return value;
};

const woltCookieOptions = getCookieOptions('cookie-consents', getCookieDomain());
const woltUidCookieOptions = getCookieOptions('wolt-uid', getCookieDomain());

const handleSubmitConsentsFailed = (error: any) => {
  let reportError: Error;
  let reportContext: object = {};
  if (error instanceof Error) {
    reportError = error;
  } else {
    reportError = new Error('Submit consents failed with unknown error');
    reportContext = { errorJson: JSON.stringify(error) };
  }

  sendErrorReport(reportError, 'GdprConsentModal', reportContext);
};

export const CookieConsentsProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [consents, setConsents] = useCookie(getCookieName('cookie-consents'));
  const [woltUid, setWoltUid] = useCookie(WOLT_UID_COOKIE_NAME);
  const { locale } = useIntl();
  const hasReceivedConsentEvent = !!consents;
  const showConsentsModal = useShowConsentsModal();
  const shouldShowConsentsModal = showConsentsModal && !hasReceivedConsentEvent;
  const contentsData = consents ? decodeCookieConsentsData(consents) : undefined;

  const generateAndSaveWoltUid = useCallback(() => {
    const hasWoltUid = Boolean(woltUid);

    if (hasWoltUid) {
      return;
    }

    const newlyGeneratedWoltUid = generateUidCookieValue();

    setWoltUid(newlyGeneratedWoltUid, woltUidCookieOptions);
  }, [setWoltUid, woltUid]);

  const updateConsents = useCallback(
    (consents: CookieConsents) => {
      setConsents(encodeCookieConsentsData(consents), woltCookieOptions);
      generateAndSaveWoltUid();
    },
    [generateAndSaveWoltUid, setConsents],
  );

  const acceptConsents = useCallback(() => {
    const allConsents = {
      functional: true,
      analytics: true,
      marketing: true,
    };
    setConsents(encodeCookieConsentsData(allConsents), woltCookieOptions);
    generateAndSaveWoltUid();
  }, [generateAndSaveWoltUid, setConsents]);

  const value = useMemo(
    () => ({
      hasReceivedConsentEvent,
      consents: contentsData ?? {
        functional: false,
        analytics: false,
        marketing: false,
      },
      updateConsents,
    }),
    [contentsData, hasReceivedConsentEvent, updateConsents],
  );

  return (
    <CookieConsentsContext.Provider value={value}>
      {children}

      <ManageCookieConsentsController
        initialConsents={value.consents}
        privacyPolicyUrl={buildPrivacyTermsUrl(locale)}
        show={shouldShowConsentsModal}
        onSubmitCookieConsents={updateConsents}
        onAcceptDefaultConsentsClick={acceptConsents}
        onSubmitConsentsFailed={handleSubmitConsentsFailed}
      />
    </CookieConsentsContext.Provider>
  );
};
